import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/shared/PageHeader";
import { RefreshingIndicator } from "@/components/shared/RefreshingIndicator";
import { StateCard } from "@/components/shared/StateCard";
import { TableSkeleton } from "@/components/shared/TableSkeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  archiveProjectAndDownload,
  createClient,
  createProject,
  createProjectPayment,
  deleteProjectPayment,
  deleteProject,
  type ProjectLabel,
  type ProjectPaymentItem,
  type ProjectPaymentMethod,
  fetchProjectPayments,
  downloadArchivedProjectPdf,
  restoreArchivedProject,
  updateProject,
  type ClientItem,
  type ProjectFinalInvoiceSummary,
  type ProjectItem,
  type ProjectStatus,
} from "@/lib/projects-clients";
import {
  invalidateClientData,
  invalidateProjectData,
} from "@/lib/query-invalidation";
import {
  queryKeys,
  useClients,
  useProjectLabels,
  useProjects,
  useTeamMembers,
} from "@/hooks/use-app-queries";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency, type ApprovalDeliveryMethod } from "@/lib/tickets";
import { sendProjectPaymentFollowup } from "@/lib/billing-exports";
import {
  AlertTriangle,
  ArrowRight,
  Download,
  FileX,
  FolderKanban,
  Info,
  Loader2,
  MoreHorizontal,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SendPaymentFollowUpDialog } from "@/components/projects/SendPaymentFollowUpDialog";
import { GenerateFinalInvoiceDialog } from "@/components/projects/GenerateFinalInvoiceDialog";
import { FinalInvoicePreviewDialog } from "@/components/projects/FinalInvoicePreviewDialog";
import { useAuth } from "@/auth/auth-context";

type ProjectForm = {
  name: string;
  code: string;
  clientId: string;
  jobSiteAddress: string;
  labelIds: string[];
};

const emptyForm: ProjectForm = {
  name: "",
  code: "",
  clientId: "",
  jobSiteAddress: "",
  labelIds: [],
};

const PROJECT_STATUSES: ProjectStatus[] = [
  "ACTIVE",
  "ON_HOLD",
  "COMPLETED",
  "CANCELLED",
];
const PAYMENT_METHOD_OPTIONS: Array<{
  value: ProjectPaymentMethod;
  label: string;
}> = [
  { value: "CASH", label: "paymentMethods.cash" },
  { value: "CHECK", label: "paymentMethods.check" },
  { value: "CARD", label: "paymentMethods.card" },
  { value: "ZELLE", label: "paymentMethods.zelle" },
  { value: "ACH", label: "paymentMethods.ach" },
  { value: "OTHER", label: "paymentMethods.other" },
];

type ProjectPaymentStatus =
  | "NO_APPROVED_TOTAL"
  | "UNPAID"
  | "PARTIALLY_PAID"
  | "PAID";

function resolveProjectPaymentStatusLabel(
  t: ReturnType<typeof useTranslation>["t"],
  project: Pick<ProjectItem, "paymentStatus" | "pendingApprovalTicketCount">,
) {
  if (
    (project.paymentStatus ?? "NO_APPROVED_TOTAL") === "NO_APPROVED_TOTAL" &&
    Number(project.pendingApprovalTicketCount ?? 0) > 0
  ) {
    return t("tickets.pending", {
      count: Number(project.pendingApprovalTicketCount ?? 0),
    });
  }

  return t(
    `projectPaymentStatus.${project.paymentStatus ?? "NO_APPROVED_TOTAL"}`,
  );
}

function paymentChipClass(status?: ProjectPaymentStatus) {
  switch (status) {
    case "UNPAID":
      return "bg-destructive/10 text-destructive";
    case "PARTIALLY_PAID":
      return "bg-warning/20 text-warning";
    case "PAID":
      return "bg-status-approved-bg text-status-approved";
    case "NO_APPROVED_TOTAL":
    default:
      return "bg-muted text-muted-foreground";
  }
}

function formatBalance(value?: number) {
  const amount = Number(value ?? 0);
  if (amount < 0) {
    return `Overpaid ${formatCurrency(Math.abs(amount))}`;
  }
  return formatCurrency(amount);
}

function assignedMemberLabel(project: ProjectItem) {
  return (
    project.assignedMember?.fullName?.trim() ||
    project.assignedMember?.email?.trim() ||
    ""
  );
}

function formatProjectScheduleSummary(
  nextSchedule: ProjectItem["nextSchedule"],
  t: ReturnType<typeof useTranslation>["t"],
) {
  if (!nextSchedule) return null;

  const startAt = new Date(nextSchedule.startAt);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isSameDay = (left: Date, right: Date) =>
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate();

  const dayLabel = isSameDay(startAt, today)
    ? t("schedule.todayShortcut")
    : isSameDay(startAt, tomorrow)
      ? t("schedule.tomorrowShortcut")
      : startAt.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        });

  const timeLabel = startAt.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  const duration =
    nextSchedule.durationMinutes && nextSchedule.durationMinutes > 0
      ? nextSchedule.durationMinutes % 60 === 0
        ? `${nextSchedule.durationMinutes / 60}h`
        : `${Math.floor(nextSchedule.durationMinutes / 60)}h ${nextSchedule.durationMinutes % 60}m`
      : null;

  return duration
    ? `${dayLabel} ${timeLabel} • ${duration}`
    : `${dayLabel} ${timeLabel}`;
}

function isProjectScheduleDone(project: ProjectItem) {
  return (
    !project.nextSchedule &&
    !!project.lastSchedule &&
    new Date(project.lastSchedule.endAt).getTime() < Date.now()
  );
}

function shouldSuggestCompletedStatus(project: ProjectItem) {
  return isProjectScheduleDone(project) && project.status === "ACTIVE";
}

function CompactProjectCard({
  project,
  hasDefinedLabels,
  actions,
  onOpen,
  onSchedule,
}: {
  project: ProjectItem;
  hasDefinedLabels: boolean;
  actions: ReactNode;
  onOpen: () => void;
  onSchedule: () => void;
}) {
  const { t } = useTranslation();
  const compactStatusLabel = (status?: ProjectStatus) =>
    t(`projectStatus.${status ?? "ACTIVE"}`);
  return (
    <div
      className="rounded-lg border bg-card p-4 cursor-pointer"
      onClick={onOpen}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex items-center gap-2">
              <FolderKanban className="h-4 w-4 shrink-0 text-muted-foreground" />
              <p
                className="truncate text-sm font-semibold text-accent"
                title={project.name}
              >
                {project.name}
              </p>
            </div>
            <span
              className={`shrink-0 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${statusChipClass(project.status)}`}
            >
              <span>{compactStatusLabel(project.status ?? "ACTIVE")}</span>
              {project.archivedAt ? (
                <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-black/10 px-1 text-[10px] font-semibold">
                  A
                </span>
              ) : null}
            </span>
          </div>
          <div className="mt-1 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="min-w-0 truncate text-xs text-muted-foreground">
                {project.client?.name ?? t("projects.noClient")}
              </p>
              <div className="mt-1">
                {project.nextSchedule ? (
                  <p className="truncate text-xs font-medium text-[#0C1F4F]">
                    {formatProjectScheduleSummary(project.nextSchedule, t)}
                  </p>
                ) : isProjectScheduleDone(project) ? (
                  <div className="flex items-center gap-2 text-xs">
                    <p className="truncate font-semibold text-emerald-700">
                      {t("schedule.status.done", "Done")}
                    </p>
                    {shouldSuggestCompletedStatus(project) ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 font-semibold text-accent hover:underline"
                        onClick={(event) => {
                          event.stopPropagation();
                          void changeProjectStatus(project, "COMPLETED");
                        }}
                      >
                        {t(
                          "projects.setStatusCompleted",
                          "Set status completed",
                        )}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="text-xs font-semibold text-accent hover:underline"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSchedule();
                    }}
                  >
                    {t("projects.setSchedule")}
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${paymentChipClass(project.paymentStatus as ProjectPaymentStatus)}`}
              >
                {resolveProjectPaymentStatusLabel(t, project)}
              </span>
              {hasDefinedLabels ? (
                <div className="mr-2 shrink-0">
                  {renderProjectLabels(project.labels) ?? (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div
          className="shrink-0 self-stretch"
          onClick={(event) => event.stopPropagation()}
        >
          {actions}
        </div>
      </div>
    </div>
  );
}

function getProjectTicketCount(
  project: ProjectItem & { _count?: { tickets?: number } },
) {
  return project._count?.tickets ?? 0;
}

function renderProjectLabels(labels?: ProjectLabel[]) {
  if (!labels?.length) return null;
  const visible = labels.slice(0, 2);
  const remaining = labels.length - visible.length;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {visible.map((label) => (
        <span
          key={label.id}
          className="inline-flex h-5 w-5 items-center justify-center rounded-full border"
          style={{
            borderColor: `${label.color}40`,
            backgroundColor: `${label.color}14`,
          }}
          title={label.name}
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: label.color }}
          />
        </span>
      ))}
      {remaining > 0 ? (
        <span
          className="inline-flex h-5 min-w-5 items-center justify-center rounded-full border px-1 text-[10px] font-medium text-muted-foreground"
          title={labels.map((label) => label.name).join(", ")}
        >
          +{remaining}
        </span>
      ) : null}
    </div>
  );
}

function statusChipClass(status?: ProjectStatus) {
  switch (status) {
    case "ACTIVE":
      return "bg-status-approved-bg text-status-approved";
    case "ON_HOLD":
      return "bg-warning/20 text-warning";
    case "COMPLETED":
      return "bg-primary/15 text-primary";
    case "CANCELLED":
      return "bg-destructive/15 text-destructive";
    case "ARCHIVED":
      return "bg-status-archived-bg text-status-archived";
    default:
      return "bg-muted text-muted-foreground";
  }
}

export default function Projects() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canManageProjects = !(
    user?.currentPlan === "TEAM" && user.role !== "ADMIN"
  );
  const canGenerateFinalInvoice = user?.role === "ADMIN" || user?.role === "PM";
  const hasTeamAssignment = user?.currentPlan === "TEAM";
  const canManageAssignments = hasTeamAssignment && user?.role === "ADMIN";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [projectHelpOpen, setProjectHelpOpen] = useState(false);
  const statusLabel = (status?: ProjectStatus) =>
    t(`projectStatus.${status ?? "ACTIVE"}`);
  const formatPaymentMethod = (method: ProjectPaymentMethod) =>
    method === "ZELLE" || method === "ACH"
      ? method
      : t(
          PAYMENT_METHOD_OPTIONS.find((option) => option.value === method)
            ?.label ?? method,
        );
  const goToProjectSchedule = (projectId: string) =>
    navigate(`/schedule?projectId=${encodeURIComponent(projectId)}`);

  const [search, setSearch] = useState("");
  const [showArchivedProjects, setShowArchivedProjects] = useState(false);
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const [projectLabelSearch, setProjectLabelSearch] = useState("");
  const statusFilterFromQuery = searchParams.get(
    "status",
  ) as ProjectStatus | null;
  const paymentFollowupOnly = searchParams.get("paymentFollowup") === "1";

  const [openModal, setOpenModal] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [form, setForm] = useState<ProjectForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [savingAndContinuing, setSavingAndContinuing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProjectItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  const [newClientEmail, setNewClientEmail] = useState("");
  const [creatingClient, setCreatingClient] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<ProjectItem | null>(null);
  const [archiveStep, setArchiveStep] = useState<1 | 2>(1);
  const [archiveStatus, setArchiveStatus] =
    useState<Exclude<ProjectStatus, "ARCHIVED">>("COMPLETED");
  const [archiving, setArchiving] = useState(false);
  const [finalInvoiceTarget, setFinalInvoiceTarget] =
    useState<ProjectItem | null>(null);
  const [finalInvoicePreviewTarget, setFinalInvoicePreviewTarget] = useState<{
    project: ProjectItem;
    invoice: ProjectFinalInvoiceSummary;
  } | null>(null);
  const [restoringProjectId, setRestoringProjectId] = useState<string | null>(
    null,
  );
  const [downloadingArchiveId, setDownloadingArchiveId] = useState<
    string | null
  >(null);
  const [paymentProject, setPaymentProject] = useState<ProjectItem | null>(
    null,
  );
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [paymentMethod, setPaymentMethod] =
    useState<ProjectPaymentMethod>("CASH");
  const [paymentNote, setPaymentNote] = useState("");
  const [savingPayment, setSavingPayment] = useState(false);
  const [statusUpdatingProjectId, setStatusUpdatingProjectId] = useState<
    string | null
  >(null);
  const [paymentsProject, setPaymentsProject] = useState<ProjectItem | null>(
    null,
  );
  const [paymentsList, setPaymentsList] = useState<ProjectPaymentItem[]>([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [deletingPaymentId, setDeletingPaymentId] = useState<string | null>(
    null,
  );
  const [sendingPaymentFollowupProjectId, setSendingPaymentFollowupProjectId] =
    useState<string | null>(null);
  const [paymentFollowupTarget, setPaymentFollowupTarget] =
    useState<ProjectItem | null>(null);
  const [assignmentTarget, setAssignmentTarget] = useState<ProjectItem | null>(
    null,
  );
  const [assignmentMemberId, setAssignmentMemberId] = useState("unassigned");
  const [savingAssignment, setSavingAssignment] = useState(false);

  const projectsQuery = useProjects({ search, labelIds: selectedLabelIds });
  const clientsQuery = useClients();
  const projectLabelsQuery = useProjectLabels();
  const teamMembersQuery = useTeamMembers(
    Boolean(assignmentTarget && canManageAssignments),
  );
  const projects = projectsQuery.data ?? [];
  const clients = clientsQuery.data ?? [];
  const projectLabels = projectLabelsQuery.data ?? [];
  const activeTeamMembers = useMemo(
    () =>
      (teamMembersQuery.data ?? []).filter(
        (member) =>
          member.kind === "user" &&
          member.status === "ACTIVE" &&
          member.isActive,
      ),
    [teamMembersQuery.data],
  );
  const loading = projectsQuery.isLoading && !projectsQuery.data;
  const error =
    projectsQuery.error instanceof Error ? projectsQuery.error.message : null;
  const isRefreshing =
    (projectsQuery.isFetching && Boolean(projectsQuery.data)) ||
    (clientsQuery.isFetching && Boolean(clientsQuery.data)) ||
    (projectLabelsQuery.isFetching && Boolean(projectLabelsQuery.data)) ||
    Boolean(statusUpdatingProjectId);

  const nextProjectCode = (offset = 1) =>
    `PRJ-${String(projects.length + offset).padStart(4, "0")}`;

  const applyClientToForm = (clientId: string) => {
    const selectedClient = clients.find((client) => client.id === clientId);
    setForm((prev) => ({
      ...prev,
      clientId,
      jobSiteAddress: selectedClient?.address?.trim() || "",
    }));
  };

  useEffect(() => {
    if (projectsQuery.isLoading) return;
    const openNew = searchParams.get("new") === "1";
    if (!openNew) return;
    const clientIdFromQuery = searchParams.get("clientId") || "";
    const isValidClient = clientIdFromQuery
      ? clients.some((client) => client.id === clientIdFromQuery)
      : false;
    setEditingProjectId(null);
    setForm({
      ...emptyForm,
      code: nextProjectCode(),
      clientId: isValidClient ? clientIdFromQuery : "",
      jobSiteAddress: isValidClient
        ? clients
            .find((client) => client.id === clientIdFromQuery)
            ?.address?.trim() || ""
        : "",
    });
    setOpenModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    next.delete("clientId");
    setSearchParams(next, { replace: true });
  }, [projectsQuery.isLoading, clients, searchParams, setSearchParams]);

  const openCreateModal = () => {
    setEditingProjectId(null);
    setProjectLabelSearch("");
    setForm({ ...emptyForm, code: nextProjectCode() });
    setOpenModal(true);
  };

  const openEditModal = (project: ProjectItem) => {
    setEditingProjectId(project.id);
    setProjectLabelSearch("");
    setForm({
      name: project.name ?? "",
      code: project.code ?? "",
      clientId: project.clientId ?? "",
      jobSiteAddress: project.jobSiteAddress ?? "",
      labelIds: (project.labels ?? []).map((label) => label.id),
    });
    setOpenModal(true);
  };

  const submitProject = async (
    mode: "save" | "continue_to_ticket" = "save",
  ) => {
    if (!form.name.trim()) {
      toast({
        title: t("projects.projectNameRequired"),
        description: t("projects.enterProjectName"),
        variant: "destructive",
      });
      return;
    }
    if (!form.clientId) {
      toast({
        title: t("projects.clientRequired"),
        description: t("projects.selectClientForProject"),
        variant: "destructive",
      });
      return;
    }

    if (mode === "continue_to_ticket") {
      setSavingAndContinuing(true);
    } else {
      setSaving(true);
    }
    try {
      if (editingProjectId) {
        await updateProject(editingProjectId, {
          name: form.name.trim(),
          code: form.code.trim() || undefined,
          clientId: form.clientId,
          jobSiteAddress: form.jobSiteAddress.trim() || undefined,
          labelIds: form.labelIds,
        });
        toast({ title: t("projects.projectUpdated") });
      } else {
        const created = await createProject({
          name: form.name.trim(),
          code: form.code.trim() || nextProjectCode(),
          clientId: form.clientId,
          jobSiteAddress: form.jobSiteAddress.trim() || undefined,
          status: "ACTIVE",
          labelIds: form.labelIds,
        });
        toast({
          title:
            mode === "continue_to_ticket"
              ? t("projects.projectCreatedContinueTicket")
              : t("projects.projectCreated"),
        });

        if (mode === "continue_to_ticket") {
          await invalidateProjectData(queryClient);
          setOpenModal(false);
          navigate(
            `/tickets/new?projectId=${encodeURIComponent(created.id)}&type=estimate`,
          );
          return;
        }
      }
      await invalidateProjectData(queryClient);
      setOpenModal(false);
    } catch (e) {
      toast({
        title: editingProjectId
          ? t("clients.updateFailed")
          : t("clients.createFailed"),
        description:
          e instanceof Error ? e.message : t("projects.unableToSaveProject"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
      setSavingAndContinuing(false);
    }
  };

  const submitClient = async () => {
    if (!newClientName.trim()) {
      toast({
        title: t("projects.clientNameRequired"),
        variant: "destructive",
      });
      return;
    }
    setCreatingClient(true);
    try {
      const client = await createClient({
        name: newClientName.trim(),
        email: newClientEmail.trim() || undefined,
        type: "General Contractor",
      });
      queryClient.setQueryData<ClientItem[]>(queryKeys.clients(""), (prev) =>
        prev ? [client, ...prev] : [client],
      );
      await invalidateClientData(queryClient);
      setForm((p) => ({ ...p, clientId: client.id }));
      setNewClientName("");
      setNewClientEmail("");
      setAddClientOpen(false);
      toast({ title: t("projects.clientCreated") });
    } catch (e) {
      toast({
        title: t("projects.createClientFailed"),
        description:
          e instanceof Error ? e.message : t("projects.unableToCreateClient"),
        variant: "destructive",
      });
    } finally {
      setCreatingClient(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if ((deleteTarget._count?.tickets ?? 0) > 0) {
      toast({
        title: t("projects.deleteBlocked"),
        description: t("projects.cannotDeleteWithTickets"),
        variant: "destructive",
      });
      setDeleteTarget(null);
      return;
    }
    setDeleting(true);
    try {
      await deleteProject(deleteTarget.id);
      toast({ title: t("projects.projectRemoved") });
      setDeleteTarget(null);
      await invalidateProjectData(queryClient);
    } catch (e) {
      toast({
        title: t("projects.deleteFailed"),
        description:
          e instanceof Error ? e.message : t("projects.unableToDeleteProject"),
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const changeProjectStatus = async (
    project: ProjectItem,
    status: ProjectStatus,
  ) => {
    setStatusUpdatingProjectId(project.id);
    try {
      await updateProject(project.id, { status });
      toast({
        title: t("projects.statusUpdated"),
        description: t("projects.statusUpdatedDescription", {
          project: project.name,
          status: statusLabel(status),
        }),
      });
      await invalidateProjectData(queryClient);
    } catch (e) {
      toast({
        title: t("projects.statusUpdateFailed"),
        description:
          e instanceof Error ? e.message : t("projects.unableToUpdateStatus"),
        variant: "destructive",
      });
    } finally {
      setStatusUpdatingProjectId(null);
    }
  };

  const toggleProjectLabel = async (project: ProjectItem, labelId: string) => {
    const current = (project.labels ?? []).map((label) => label.id);
    const nextLabelIds = current.includes(labelId)
      ? current.filter((id) => id !== labelId)
      : [...current, labelId];

    try {
      await updateProject(project.id, { labelIds: nextLabelIds });
      toast({ title: t("projects.labelsUpdated") });
      await invalidateProjectData(queryClient);
    } catch (e) {
      toast({
        title: t("projects.labelUpdateFailed"),
        description:
          e instanceof Error ? e.message : t("projects.unableToUpdateLabels"),
        variant: "destructive",
      });
    }
  };

  const openAssignmentModal = (project: ProjectItem) => {
    setAssignmentTarget(project);
    setAssignmentMemberId(project.assignedMember?.id ?? "unassigned");
  };

  const closeAssignmentModal = () => {
    if (savingAssignment) return;
    setAssignmentTarget(null);
    setAssignmentMemberId("unassigned");
  };

  const saveAssignment = async () => {
    if (!assignmentTarget) return;

    setSavingAssignment(true);
    try {
      await updateProject(assignmentTarget.id, {
        assignedMemberId:
          assignmentMemberId === "unassigned" ? "" : assignmentMemberId,
      });
      toast({
        title: t("projects.assignmentUpdated", "Project assignment updated"),
      });
      setAssignmentTarget(null);
      setAssignmentMemberId("unassigned");
      await invalidateProjectData(queryClient);
    } catch (error) {
      toast({
        title: t(
          "projects.assignmentUpdateFailed",
          "Could not update assignment",
        ),
        description:
          error instanceof Error
            ? error.message
            : t("common.tryAgain", "Please try again."),
        variant: "destructive",
      });
    } finally {
      setSavingAssignment(false);
    }
  };

  const renderProjectMenuContent = (project: ProjectItem, scopeKey: string) => {
    if (project.archivedAt) {
      return (
        <>
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation();
              void downloadArchivePdf(project);
            }}
            disabled={downloadingArchiveId === project.id}
          >
            {t("projects.downloadPdf")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation();
              void restoreProject(project);
            }}
            disabled={restoringProjectId === project.id}
          >
            {t("projects.activeAgain")}
          </DropdownMenuItem>
        </>
      );
    }

    return (
      <>
        {canManageProjects ? (
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation();
              openEditModal(project);
            }}
          >
            {t("projects.edit")}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          onClick={(event) => {
            event.stopPropagation();
            goToProjectSchedule(project.id);
          }}
        >
          {t("projects.setSchedule")}
        </DropdownMenuItem>
        {canManageAssignments ? (
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation();
              openAssignmentModal(project);
            }}
          >
            {t("projects.changeAssignment", "Change assignment")}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={(event) => {
            event.stopPropagation();
            openRecordPayment(project);
          }}
        >
          {t("projects.recordPayment")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(event) => {
            event.stopPropagation();
            void openPaymentsList(project);
          }}
        >
          {t("projects.viewPayments")}
        </DropdownMenuItem>
        {Number(project.balance ?? 0) > 0 ? (
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation();
              setPaymentFollowupTarget(project);
            }}
            disabled={sendingPaymentFollowupProjectId === project.id}
          >
            {t("projects.sendPaymentFollowUp")}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        {canManageProjects && projectLabels.length ? (
          <>
            {projectLabels.map((label) => {
              const selected = (project.labels ?? []).some(
                (item) => item.id === label.id,
              );
              return (
                <DropdownMenuItem
                  key={`${scopeKey}-label-${label.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    void toggleProjectLabel(project, label.id);
                  }}
                >
                  <span
                    className="mr-2 inline-flex h-2 w-2 rounded-full"
                    style={{ backgroundColor: label.color }}
                  />
                  <span className="flex-1 truncate">{label.name}</span>
                  {selected ? (
                    <span className="text-xs text-muted-foreground">
                      {t("projects.selected")}
                    </span>
                  ) : null}
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
          </>
        ) : null}
        {canManageProjects ? (
          <>
            {PROJECT_STATUSES.map((status) => (
              <DropdownMenuItem
                key={`${scopeKey}-status-${status}`}
                onClick={(event) => {
                  event.stopPropagation();
                  void changeProjectStatus(project, status);
                }}
              >
                {t("projects.setStatus", { status: statusLabel(status) })}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            {canGenerateFinalInvoice ? (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  setFinalInvoiceTarget(project);
                }}
              >
                Generate final invoice
              </DropdownMenuItem>
            ) : null}
            {(project._count?.tickets ?? 0) > 0 ? (
              <>
                <DropdownMenuItem
                  onClick={(event) => {
                    event.stopPropagation();
                    archiveProject(project);
                  }}
                >
                  {t("projects.archiveProject")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled
                  className="text-xs text-muted-foreground"
                >
                  {t("projects.cannotDeleteWithTickets")}
                </DropdownMenuItem>
              </>
            ) : (
              <>
                <DropdownMenuItem
                  onClick={(event) => {
                    event.stopPropagation();
                    archiveProject(project);
                  }}
                >
                  {t("projects.archiveProject")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={(event) => {
                    event.stopPropagation();
                    setDeleteTarget(project);
                  }}
                >
                  {t("common.delete")}
                </DropdownMenuItem>
              </>
            )}
          </>
        ) : null}
      </>
    );
  };

  const archiveProject = (project: ProjectItem) => {
    if (project.paymentStatus !== "PAID") {
      toast({
        title: t(
          "projects.archiveBlockedPaymentTitle",
          "Project cannot be archived",
        ),
        description: t(
          "projects.archiveBlockedPaymentDescription",
          "This project must be fully paid before it can be archived.",
        ),
        variant: "destructive",
      });
      return;
    }

    setArchiveTarget(project);
    setArchiveStep(1);
    setArchiveStatus("COMPLETED");
  };

  const closeArchiveModal = () => {
    if (archiving) return;
    setArchiveTarget(null);
    setArchiveStep(1);
    setArchiveStatus("COMPLETED");
  };

  const confirmArchiveAndDownload = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      const { blob, fileName } = await archiveProjectAndDownload(
        archiveTarget.id,
        {
          finalStatus: archiveStatus,
        },
      );
      triggerBlobDownload(blob, fileName);
      toast({
        title: t("projects.projectArchived"),
        description: t("projects.projectArchivedDescription", {
          project: archiveTarget.name,
        }),
      });
      closeArchiveModal();
      await invalidateProjectData(queryClient);
    } catch (e) {
      toast({
        title: t("projects.archiveFailed"),
        description:
          e instanceof Error ? e.message : t("projects.unableToArchiveProject"),
        variant: "destructive",
      });
    } finally {
      setArchiving(false);
    }
  };

  const triggerBlobDownload = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadArchivePdf = async (project: ProjectItem) => {
    setDownloadingArchiveId(project.id);
    try {
      const { blob, fileName } = await downloadArchivedProjectPdf(project.id);
      triggerBlobDownload(blob, fileName);
    } catch (e) {
      toast({
        title: t("projects.downloadFailed"),
        description:
          e instanceof Error
            ? e.message
            : t("projects.unableToDownloadArchivePdf"),
        variant: "destructive",
      });
    } finally {
      setDownloadingArchiveId(null);
    }
  };

  const restoreProject = async (project: ProjectItem) => {
    setRestoringProjectId(project.id);
    try {
      await restoreArchivedProject(project.id);
      toast({
        title: t("projects.projectReactivated"),
        description: t("projects.projectReactivatedDescription", {
          project: project.name,
        }),
      });
      await invalidateProjectData(queryClient);
    } catch (e) {
      toast({
        title: t("projects.restoreFailed"),
        description:
          e instanceof Error
            ? e.message
            : t("projects.unableToReactivateProject"),
        variant: "destructive",
      });
    } finally {
      setRestoringProjectId(null);
    }
  };

  const openRecordPayment = (project: ProjectItem) => {
    setPaymentProject(project);
    setPaymentAmount("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentMethod("CASH");
    setPaymentNote("");
  };

  const closeRecordPayment = () => {
    if (savingPayment) return;
    setPaymentProject(null);
    setPaymentAmount("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentMethod("CASH");
    setPaymentNote("");
  };

  const submitPayment = async () => {
    if (!paymentProject) return;

    const amount = Number(paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({
        title: t("projects.invalidAmount"),
        description: t("projects.amountGreaterThanZero"),
        variant: "destructive",
      });
      return;
    }

    if (!paymentDate) {
      toast({
        title: t("projects.paymentDateRequired"),
        variant: "destructive",
      });
      return;
    }

    setSavingPayment(true);
    try {
      await createProjectPayment(paymentProject.id, {
        amount,
        paymentDate,
        method: paymentMethod,
        note: paymentNote.trim() || undefined,
      });
      toast({
        title: t("projects.paymentRecorded"),
        description: t("projects.paymentRecordedDescription", {
          amount: formatCurrency(amount),
          project: paymentProject.name,
        }),
      });
      closeRecordPayment();
      await invalidateProjectData(queryClient);
    } catch (e) {
      toast({
        title: t("projects.paymentFailed"),
        description:
          e instanceof Error ? e.message : t("projects.unableToRecordPayment"),
        variant: "destructive",
      });
    } finally {
      setSavingPayment(false);
    }
  };

  const openPaymentsList = async (project: ProjectItem) => {
    setPaymentsProject(project);
    setLoadingPayments(true);
    try {
      const response = await fetchProjectPayments(project.id);
      setPaymentsList(response.items);
      setPaymentsTotal(response.totalPaid);
    } catch (e) {
      toast({
        title: t("projects.failedToLoadPayments"),
        description:
          e instanceof Error ? e.message : t("projects.unableToLoadPayments"),
        variant: "destructive",
      });
      setPaymentsProject(null);
      setPaymentsList([]);
      setPaymentsTotal(0);
    } finally {
      setLoadingPayments(false);
    }
  };

  const closePaymentsList = () => {
    if (deletingPaymentId) return;
    setPaymentsProject(null);
    setPaymentsList([]);
    setPaymentsTotal(0);
    setLoadingPayments(false);
  };

  const removePayment = async (payment: ProjectPaymentItem) => {
    if (!paymentsProject) return;
    const confirmed = window.confirm(
      t("projects.confirmDeletePayment", {
        amount: formatCurrency(payment.amount),
        project: paymentsProject.name,
      }),
    );
    if (!confirmed) return;

    setDeletingPaymentId(payment.id);
    try {
      await deleteProjectPayment(paymentsProject.id, payment.id);
      const next = paymentsList.filter((item) => item.id !== payment.id);
      setPaymentsList(next);
      setPaymentsTotal(
        next.reduce((sum, item) => sum + Number(item.amount ?? 0), 0),
      );
      toast({ title: t("projects.paymentDeleted") });
      await invalidateProjectData(queryClient);
    } catch (e) {
      toast({
        title: t("projects.deleteFailed"),
        description:
          e instanceof Error ? e.message : t("projects.unableToDeletePayment"),
        variant: "destructive",
      });
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const sendPaymentFollowupForProject = async (
    project: ProjectItem,
    deliveryMethod: ApprovalDeliveryMethod,
    smsConsentConfirmed?: boolean,
  ) => {
    setSendingPaymentFollowupProjectId(project.id);
    try {
      const result = await sendProjectPaymentFollowup(project.id, {
        deliveryMethod,
        smsConsentConfirmed,
      });
      const sentLabel =
        result.sentChannels.length === 2
          ? "Email and SMS sent"
          : result.sentChannels[0] === "sms"
            ? "SMS sent"
            : "Email sent";
      const failureLabel = result.failedChannels.length
        ? ` Failed: ${result.failedChannels.map((item) => item.channel.toUpperCase()).join(", ")}.`
        : "";
      toast({
        title: t("projects.paymentFollowUpSent"),
        description: `${sentLabel}.${failureLabel}`,
      });
      setPaymentFollowupTarget(null);
    } catch (e) {
      toast({
        title: t("projects.followUpFailed"),
        description:
          e instanceof Error
            ? e.message
            : t("projects.unableToSendPaymentFollowUp"),
        variant: "destructive",
      });
    } finally {
      setSendingPaymentFollowupProjectId(null);
    }
  };

  const visibleProjects = (
    showArchivedProjects
      ? projects
      : projects.filter((project) => !project.archivedAt)
  ).filter((project) => {
    if (statusFilterFromQuery && project.status !== statusFilterFromQuery) {
      return false;
    }
    if (paymentFollowupOnly) {
      return project.status === "COMPLETED" && Number(project.balance ?? 0) > 0;
    }
    return true;
  });

  const filteredProjectLabels = projectLabels.filter((label) =>
    label.name.toLowerCase().includes(projectLabelSearch.trim().toLowerCase()),
  );
  const hasDefinedLabels = projectLabels.length > 0;

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <span>{t("pages.projects.title")}</span>
            <Popover open={projectHelpOpen} onOpenChange={setProjectHelpOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-transparent text-muted-foreground transition-colors hover:bg-muted/30 hover:text-[#0C1F4F]"
                  aria-label={t("projects.help.ariaLabel")}
                >
                  <Info className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[360px] border-border/80 bg-white p-4 shadow-lg"
              >
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-[#0C1F4F]">
                      {t("projects.help.title")}
                    </p>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      {t("projects.help.subtitle")}
                    </p>
                  </div>
                  <ul className="space-y-2 text-sm leading-6 text-muted-foreground">
                    <li>{t("projects.help.items.relationship")}</li>
                    <li>{t("projects.help.items.status")}</li>
                    <li>{t("projects.help.items.billing")}</li>
                    <li>{t("projects.help.items.scheduling")}</li>
                  </ul>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        }
        actions={
          <Button
            onClick={openCreateModal}
            className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5"
          >
            <Plus className="h-4 w-4" /> {t("projects.addProject")}
          </Button>
        }
      />

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3 md:flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative max-w-md w-full flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("projects.searchPlaceholder")}
                className="pl-9 h-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="hidden md:block">
              <RefreshingIndicator visible={isRefreshing} />
            </div>
          </div>
          <div className="flex w-full items-center md:hidden">
            <RefreshingIndicator visible={isRefreshing} />
            <label className="ml-auto inline-flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border accent-[hsl(var(--accent))]"
                checked={showArchivedProjects}
                onChange={(e) => setShowArchivedProjects(e.target.checked)}
              />
              <span>{t("projects.showArchivedProjects")}</span>
            </label>
          </div>
        </div>
        <div className="hidden md:flex md:w-auto md:items-center md:gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border accent-[hsl(var(--accent))]"
              checked={showArchivedProjects}
              onChange={(e) => setShowArchivedProjects(e.target.checked)}
            />
            <span>{t("projects.showArchivedProjects")}</span>
          </label>
        </div>
      </div>

      {statusFilterFromQuery || paymentFollowupOnly ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
          <span>{t("projects.dashboardFilterApplied")}</span>
          {statusFilterFromQuery ? (
            <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium text-foreground">
              {t("projects.statusFilter", {
                status: statusLabel(statusFilterFromQuery),
              })}
            </span>
          ) : null}
          {paymentFollowupOnly ? (
            <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium text-foreground">
              {t("projects.completedOutstandingBalance")}
            </span>
          ) : null}
          <button
            type="button"
            className="text-xs font-medium text-accent hover:underline"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("status");
              next.delete("paymentFollowup");
              setSearchParams(next, { replace: true });
            }}
          >
            {t("common.clear")}
          </button>
        </div>
      ) : null}

      <SendPaymentFollowUpDialog
        open={Boolean(paymentFollowupTarget)}
        onOpenChange={(open) => {
          if (!open) setPaymentFollowupTarget(null);
        }}
        target={
          paymentFollowupTarget
            ? {
                projectName: paymentFollowupTarget.name,
                clientName: paymentFollowupTarget.client?.name ?? null,
                clientEmail: paymentFollowupTarget.client?.email ?? null,
                clientPhone: paymentFollowupTarget.client?.phone ?? null,
              }
            : null
        }
        loading={
          Boolean(paymentFollowupTarget) &&
          sendingPaymentFollowupProjectId === paymentFollowupTarget?.id
        }
        smsAllowedByPlan={user?.currentPlan !== "STARTER"}
        onConfirm={async (deliveryMethod, smsConsentConfirmed) => {
          if (!paymentFollowupTarget) return;
          await sendPaymentFollowupForProject(
            paymentFollowupTarget,
            deliveryMethod,
            smsConsentConfirmed,
          );
        }}
      />

      {projectLabels.length ? (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("projects.filterByLabels")}
          </p>
          <div className="flex flex-wrap gap-2">
            {projectLabels.map((label) => {
              const selected = selectedLabelIds.includes(label.id);
              return (
                <button
                  key={label.id}
                  type="button"
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${selected ? "shadow-sm" : "hover:bg-muted/50"}`}
                  style={{
                    color: label.color,
                    borderColor: selected ? label.color : `${label.color}50`,
                    backgroundColor: selected
                      ? `${label.color}18`
                      : "transparent",
                  }}
                  onClick={() =>
                    setSelectedLabelIds((prev) =>
                      prev.includes(label.id)
                        ? prev.filter((id) => id !== label.id)
                        : [...prev, label.id],
                    )
                  }
                >
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: label.color }}
                  />
                  {label.name}
                </button>
              );
            })}
            {selectedLabelIds.length ? (
              <button
                type="button"
                className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted/50"
                onClick={() => setSelectedLabelIds([])}
              >
                {t("common.clear")}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {error ? (
        <StateCard
          icon={AlertTriangle}
          tone="error"
          title={t("projects.failedToLoad")}
          description={error}
          primaryActionLabel={t("common.retry")}
          onPrimaryAction={() => void projectsQuery.refetch()}
        />
      ) : loading ? (
        <TableSkeleton rows={5} columns={hasTeamAssignment ? 10 : 9} />
      ) : visibleProjects.length === 0 ? (
        <StateCard
          icon={FileX}
          title={t("projects.noProjects")}
          description={
            showArchivedProjects
              ? t("projects.noProjectsDescription")
              : t("projects.noActiveProjectsDescription")
          }
        />
      ) : (
        <>
          <div className="hidden min-[1140px]:block rounded-lg border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="bg-muted/35 px-4 py-3 text-left text-[#0C1F4F]">
                    {t("projects.projectName")}
                  </th>
                  <th className="border-l border-border/60 px-4 py-3 text-left text-[#0C1F4F]">
                    {t("projects.client")}
                  </th>
                  {hasTeamAssignment ? (
                    <th className="border-l border-border/60 bg-muted/35 px-4 py-3 text-left text-[#0C1F4F]">
                      {t("projects.assignedTo", "Assigned to")}
                    </th>
                  ) : null}
                  <th className="border-l border-border/60 bg-muted/35 px-4 py-3 text-center text-[#0C1F4F]">
                    {t("projects.tickets")}
                  </th>
                  <th className="border-l border-border/60 px-4 py-3 text-left text-[#0C1F4F]">
                    {t("projects.schedule")}
                  </th>
                  <th className="border-l border-border/60 bg-muted/35 px-4 py-3 text-center text-[#0C1F4F]">
                    {t("team.status")}
                  </th>
                  {hasDefinedLabels ? (
                    <th className="border-l border-border/60 px-4 py-3 text-left text-[#0C1F4F]">
                      {t("projects.labels")}
                    </th>
                  ) : null}
                  <th
                    className={`border-l border-border/60 px-4 py-3 text-right text-[#0C1F4F] ${hasDefinedLabels ? "bg-muted/35" : ""}`}
                  >
                    {t("projects.balance")}
                  </th>
                  <th className="border-l border-border/60 bg-muted/35 px-4 py-3 text-center text-[#0C1F4F]">
                    {t("projects.paymentStatus")}
                  </th>
                  <th className="w-10 border-l border-border/60 px-4 py-3 text-[#0C1F4F]"></th>
                </tr>
              </thead>
              <tbody>
                {visibleProjects.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                    onClick={() => navigate(`/projects/${p.id}`)}
                  >
                    <td className="bg-muted/35 px-4 py-3">
                      <p className="block max-w-full text-left font-semibold text-accent">
                        {p.name}
                      </p>
                    </td>
                    <td className="border-l border-border/50 px-4 py-3 text-muted-foreground">
                      {p.client?.name ?? "-"}
                    </td>
                    {hasTeamAssignment ? (
                      <td className="border-l border-border/50 bg-muted/35 px-4 py-3">
                        {assignedMemberLabel(p) ? (
                          <span className="font-medium text-[#0C1F4F]">
                            {assignedMemberLabel(p)}
                          </span>
                        ) : (
                          <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                            {t("projects.unassigned", "Unassigned")}
                          </span>
                        )}
                      </td>
                    ) : null}
                    <td className="border-l border-border/50 bg-muted/35 px-4 py-3 text-center">
                      {getProjectTicketCount(p)}
                    </td>
                    <td className="border-l border-border/50 px-4 py-3">
                      {p.nextSchedule ? (
                        <div className="space-y-1 text-left">
                          <p className="text-sm font-medium text-[#0C1F4F]">
                            {formatProjectScheduleSummary(p.nextSchedule, t)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t(
                              `schedule.status.${p.nextSchedule.status.toLowerCase()}`,
                              p.nextSchedule.status,
                            )}
                          </p>
                        </div>
                      ) : isProjectScheduleDone(p) ? (
                        <div className="space-y-1 text-left">
                          <p className="text-sm font-semibold text-emerald-700">
                            {t("schedule.status.done", "Done")}
                          </p>
                          {shouldSuggestCompletedStatus(p) ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline"
                              onClick={(event) => {
                                event.stopPropagation();
                                void changeProjectStatus(p, "COMPLETED");
                              }}
                            >
                              {t(
                                "projects.setStatusCompleted",
                                "Set status completed",
                              )}
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          ) : null}
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          onClick={(event) => {
                            event.stopPropagation();
                            goToProjectSchedule(p.id);
                          }}
                        >
                          {t("projects.setSchedule")}
                        </Button>
                      )}
                    </td>
                    <td className="border-l border-border/50 bg-muted/35 px-4 py-3 text-center">
                      <div className="flex justify-center">
                        {p.archivedAt ? (
                          <span
                            className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${statusChipClass(p.status)}`}
                          >
                            <span>{statusLabel(p.status ?? "ACTIVE")}</span>
                            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-black/10 px-1 text-[10px] font-semibold">
                              A
                            </span>
                          </span>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${statusChipClass(p.status)} hover:opacity-90`}
                                onClick={(event) => event.stopPropagation()}
                              >
                                <span>{statusLabel(p.status ?? "ACTIVE")}</span>
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="center">
                              {PROJECT_STATUSES.map((status) => (
                                <DropdownMenuItem
                                  key={status}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void changeProjectStatus(p, status);
                                  }}
                                >
                                  {statusLabel(status)}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </td>
                    {hasDefinedLabels ? (
                      <td className="border-l border-border/50 px-4 py-3">
                        {renderProjectLabels(p.labels) ?? (
                          <span className="text-xs text-muted-foreground">
                            —
                          </span>
                        )}
                      </td>
                    ) : null}
                    <td
                      className={`border-l border-border/50 px-4 py-3 text-right ${hasDefinedLabels ? "bg-muted/35" : ""}`}
                    >
                      {formatBalance(p.balance)}
                    </td>
                    <td className="border-l border-border/50 bg-muted/35 px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${paymentChipClass(p.paymentStatus as ProjectPaymentStatus)}`}
                      >
                        {resolveProjectPaymentStatusLabel(t, p)}
                      </span>
                    </td>
                    <td
                      className="w-[50px] border-l border-border/50 p-0"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="flex h-full min-h-[72px] w-full items-center justify-center rounded-none hover:bg-secondary/60"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {renderProjectMenuContent(p, `desktop-${p.id}`)}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="hidden min-[767px]:block min-[1140px]:hidden space-y-3">
            {visibleProjects.map((p) => (
              <CompactProjectCard
                key={`tablet-${p.id}`}
                project={p}
                hasDefinedLabels={hasDefinedLabels}
                onOpen={() => navigate(`/projects/${p.id}`)}
                onSchedule={() => goToProjectSchedule(p.id)}
                actions={
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex h-full min-h-[56px] w-12 items-center justify-center rounded-md hover:bg-secondary/60"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {renderProjectMenuContent(p, `tablet-${p.id}`)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                }
              />
            ))}
          </div>

          <div className="min-[767px]:hidden space-y-3">
            {visibleProjects.map((p) => (
              <CompactProjectCard
                key={`mobile-${p.id}`}
                project={p}
                hasDefinedLabels={hasDefinedLabels}
                onOpen={() => navigate(`/projects/${p.id}`)}
                onSchedule={() => goToProjectSchedule(p.id)}
                actions={
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex h-full min-h-[56px] w-12 items-center justify-center rounded-md hover:bg-secondary/60"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {renderProjectMenuContent(p, `mobile-${p.id}`)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                }
              />
            ))}
          </div>
        </>
      )}

      <Dialog
        open={Boolean(assignmentTarget)}
        onOpenChange={(open) => !open && closeAssignmentModal()}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0C1F4F]">
              {t(
                "projects.changeProjectAssignment",
                "Change project assignment",
              )}
            </DialogTitle>
            <DialogDescription className="text-[#0C1F4F]/80">
              {t(
                "projects.changeProjectAssignmentDescription",
                "Update who is responsible for this project.",
              )}
            </DialogDescription>
          </DialogHeader>

          {assignmentTarget ? (
            <div className="space-y-4">
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("projects.projectName")}
                </p>
                <p className="mt-1 font-semibold text-[#0C1F4F]">
                  {assignmentTarget.name}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {t("projects.currentAssignee", "Current assignee")}:{" "}
                  <span className="font-medium text-[#0C1F4F]">
                    {assignedMemberLabel(assignmentTarget) ||
                      t("projects.unassigned", "Unassigned")}
                  </span>
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[#0C1F4F]">
                  {t("projects.assignedTo", "Assigned to")}
                </Label>
                <Select
                  value={assignmentMemberId}
                  onValueChange={setAssignmentMemberId}
                  disabled={savingAssignment}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("projects.unassigned", "Unassigned")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">
                      {t("projects.unassigned", "Unassigned")}
                    </SelectItem>
                    {activeTeamMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {(member.fullName?.trim() || member.email) +
                          ` - ${member.role}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!teamMembersQuery.isLoading &&
                activeTeamMembers.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "projects.noActiveTeamMembers",
                      "No active team members available.",
                    )}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          <DialogFooter className="gap-2.5">
            <Button
              type="button"
              variant="outline"
              onClick={closeAssignmentModal}
              disabled={savingAssignment}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => void saveAssignment()}
              disabled={savingAssignment}
            >
              {savingAssignment ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("common.saving", "Saving...")}
                </>
              ) : (
                t("projects.saveAssignment", "Save assignment")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[#0C1F4F]">
              {editingProjectId
                ? t("projects.editProject")
                : t("projects.addProject")}
            </DialogTitle>
            <DialogDescription className="text-[#0C1F4F]/80">
              Enter project details for ticketing, approvals and billing
              workflows.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[#0C1F4F]">
                {t("projects.projectName")}{" "}
                <span className="text-accent">*</span>
              </Label>
              <Input
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
                placeholder={t("projects.projectNamePlaceholder")}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[#0C1F4F]">
                  {t("projects.projectCode")}
                </Label>
                <Input
                  value={form.code}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, code: e.target.value }))
                  }
                  placeholder={nextProjectCode()}
                />
              </div>
              <div className="space-y-1.5 pt-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[#0C1F4F]">
                    {t("projects.client")}{" "}
                    <span className="text-accent">*</span>
                  </Label>
                  <button
                    type="button"
                    className="text-xs text-accent hover:underline"
                    onClick={() => setAddClientOpen(true)}
                  >
                    + {t("clients.addClient")}
                  </button>
                </div>
                {clients.length === 0 ? (
                  <div className="rounded-md border p-2 text-sm text-muted-foreground">
                    {t("clients.noClientsYet")}
                  </div>
                ) : (
                  <select
                    className="w-full h-10 rounded-md border bg-background px-3 text-sm"
                    value={form.clientId}
                    onChange={(e) => applyClientToForm(e.target.value)}
                  >
                    <option value="">{t("createTicket.selectClient")}</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#0C1F4F]">
                {t("projects.jobSiteAddress")}
              </Label>
              <Input
                value={form.jobSiteAddress}
                onChange={(e) =>
                  setForm((p) => ({ ...p, jobSiteAddress: e.target.value }))
                }
                placeholder={t("clients.addressPlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#0C1F4F]">{t("projects.labels")}</Label>
              {form.labelIds.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {form.labelIds
                    .map((labelId) =>
                      projectLabels.find((label) => label.id === labelId),
                    )
                    .filter((label): label is ProjectLabel => Boolean(label))
                    .map((label) => (
                      <span
                        key={label.id}
                        className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium"
                        style={{
                          color: label.color,
                          borderColor: `${label.color}40`,
                          backgroundColor: `${label.color}14`,
                        }}
                      >
                        <span
                          className="mr-1 h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: label.color }}
                        />
                        {label.name}
                      </span>
                    ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t("projects.noLabelsSelected")}
                </p>
              )}

              {projectLabels.length ? (
                <>
                  <Input
                    value={projectLabelSearch}
                    onChange={(e) => setProjectLabelSearch(e.target.value)}
                    placeholder={t("projects.searchLabels")}
                  />
                  <div className="max-h-40 overflow-y-auto rounded-md border">
                    {filteredProjectLabels.length ? (
                      filteredProjectLabels.map((label) => {
                        const checked = form.labelIds.includes(label.id);
                        return (
                          <label
                            key={label.id}
                            className="flex cursor-pointer items-center justify-between gap-3 border-b px-3 py-2 text-sm last:border-b-0 hover:bg-muted/30"
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  setForm((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          labelIds: checked
                                            ? prev.labelIds.filter(
                                                (id) => id !== label.id,
                                              )
                                            : [...prev.labelIds, label.id],
                                        }
                                      : prev,
                                  )
                                }
                              />
                              <span
                                className="inline-flex h-2 w-2 rounded-full"
                                style={{ backgroundColor: label.color }}
                              />
                              <span className="truncate">{label.name}</span>
                            </span>
                            <span
                              className="inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium"
                              style={{
                                color: label.color,
                                borderColor: `${label.color}40`,
                                backgroundColor: `${label.color}14`,
                              }}
                            >
                              {label.name}
                            </span>
                          </label>
                        );
                      })
                    ) : (
                      <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                        {t("projects.noLabelsMatch")}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t("projects.createLabelsInSettings")}
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2.5">
            <Button variant="outline" onClick={() => setOpenModal(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="outline"
              onClick={() => void submitProject("save")}
              disabled={saving || savingAndContinuing}
            >
              {saving
                ? t("common.saving")
                : editingProjectId
                  ? t("common.saveChanges")
                  : t("projects.addProject")}
            </Button>
            {!editingProjectId ? (
              <Button
                onClick={() => void submitProject("continue_to_ticket")}
                disabled={saving || savingAndContinuing}
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                {savingAndContinuing
                  ? t("common.saving")
                  : t("projects.continueToTicket")}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addClientOpen} onOpenChange={setAddClientOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("clients.addClient")}</DialogTitle>
            <DialogDescription>
              {t("projects.createClientAndLink")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t("clients.clientName")} *</Label>
              <Input
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                placeholder={t("clients.clientNamePlaceholder")}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("createTicket.primaryApproverEmailOptional")}</Label>
              <Input
                value={newClientEmail}
                onChange={(e) => setNewClientEmail(e.target.value)}
                type="email"
                placeholder={t("clients.primaryApproverEmailPlaceholder")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddClientOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={submitClient}
              disabled={creatingClient}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {creatingClient ? t("common.saving") : t("clients.addClient")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("projects.deleteProject")}</DialogTitle>
            <DialogDescription>
              {deleteTarget
                ? t("clients.confirmDeleteClient", { name: deleteTarget.name })
                : t("common.areYouSure")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? t("common.deleting") : t("projects.deleteProject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!archiveTarget}
        onOpenChange={(open) => !open && closeArchiveModal()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("projects.archiveProject")}</DialogTitle>
            <DialogDescription>
              {archiveStep === 1
                ? t("projects.archiveIntro")
                : t("projects.archiveFinalize")}
            </DialogDescription>
          </DialogHeader>

          {archiveStep === 1 ? (
            <div className="space-y-3">
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-foreground">
                {archiveTarget?.name}
              </div>
              <div className="space-y-1.5">
                <Label>{t("projects.finalStatus")}</Label>
                <select
                  className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                  value={archiveStatus}
                  onChange={(e) =>
                    setArchiveStatus(
                      e.target.value as Exclude<ProjectStatus, "ARCHIVED">,
                    )
                  }
                >
                  {PROJECT_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {statusLabel(status)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                {t("projects.archivePdfIncludes")}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={
                archiveStep === 1 ? closeArchiveModal : () => setArchiveStep(1)
              }
              disabled={archiving}
            >
              {archiveStep === 1 ? t("common.cancel") : t("projects.back")}
            </Button>
            {archiveStep === 1 ? (
              <Button
                onClick={() => setArchiveStep(2)}
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                {t("projects.continue")}
              </Button>
            ) : (
              <Button
                onClick={() => void confirmArchiveAndDownload()}
                disabled={archiving}
                className="bg-accent text-accent-foreground hover:bg-accent/90 gap-2"
              >
                {archiving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {archiving
                  ? t("projects.generating")
                  : t("projects.archiveAndDownloadPdf")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GenerateFinalInvoiceDialog
        projectId={finalInvoiceTarget?.id ?? null}
        projectName={finalInvoiceTarget?.name}
        open={Boolean(finalInvoiceTarget)}
        onOpenChange={(open) => {
          if (!open) setFinalInvoiceTarget(null);
        }}
        onGenerated={(invoice) => {
          if (finalInvoiceTarget) {
            setFinalInvoicePreviewTarget({
              project: finalInvoiceTarget,
              invoice,
            });
          }
          void invalidateProjectData(queryClient);
        }}
      />

      <FinalInvoicePreviewDialog
        projectId={finalInvoicePreviewTarget?.project.id ?? null}
        projectName={finalInvoicePreviewTarget?.project.name}
        clientName={finalInvoicePreviewTarget?.project.client?.name}
        clientEmail={finalInvoicePreviewTarget?.project.client?.email}
        invoice={finalInvoicePreviewTarget?.invoice ?? null}
        open={Boolean(finalInvoicePreviewTarget)}
        onOpenChange={(open) => {
          if (!open) setFinalInvoicePreviewTarget(null);
        }}
        onSent={(invoice) => {
          setFinalInvoicePreviewTarget((current) =>
            current ? { ...current, invoice } : current,
          );
          void invalidateProjectData(queryClient);
        }}
      />

      <Dialog
        open={!!paymentProject}
        onOpenChange={(open) => !open && closeRecordPayment()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("projects.recordPaymentForProject")}</DialogTitle>
            <DialogDescription>
              {t("projects.recordPaymentForProjectDescription", {
                project:
                  paymentProject?.name ??
                  t("projects.projectName").toLowerCase(),
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t("projects.amount")}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder={t("projects.amountPlaceholder")}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>{t("projects.paymentDate")}</Label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("projects.method")}</Label>
                <select
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  value={paymentMethod}
                  onChange={(e) =>
                    setPaymentMethod(e.target.value as ProjectPaymentMethod)
                  }
                >
                  {PAYMENT_METHOD_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.label)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>{t("projects.noteOptional")}</Label>
              <Textarea
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
                rows={3}
                placeholder={t("projects.optionalNotePlaceholder")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeRecordPayment}
              disabled={savingPayment}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => void submitPayment()}
              disabled={savingPayment}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {savingPayment ? t("common.saving") : t("common.saveChanges")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!paymentsProject}
        onOpenChange={(open) => !open && closePaymentsList()}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("projects.viewProjectPayments")}</DialogTitle>
            <DialogDescription>
              {paymentsProject?.name ?? t("common.project")}{" "}
              {t("projects.viewPayments").toLowerCase()}
            </DialogDescription>
          </DialogHeader>

          {loadingPayments ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {t("clients.loadingProjects")}
            </div>
          ) : paymentsList.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {t("projects.noPaymentsRecorded")}
            </div>
          ) : (
            <div className="max-h-[55vh] overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b bg-muted/70 backdrop-blur supports-[backdrop-filter]:bg-muted/60">
                  <tr>
                    <th className="px-3 py-2 text-left">
                      {t("clientDetail.date")}
                    </th>
                    <th className="px-3 py-2 text-right">
                      {t("clientDetail.amount")}
                    </th>
                    <th className="px-3 py-2 text-left">
                      {t("projects.method")}
                    </th>
                    <th className="px-3 py-2 text-left">{t("clients.note")}</th>
                    <th className="px-3 py-2 text-left">
                      {t("createTicket.addedBy")}
                    </th>
                    <th className="w-10 px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {paymentsList.map((payment) => (
                    <tr key={payment.id} className="border-b last:border-0">
                      <td className="px-3 py-2 text-muted-foreground">
                        {new Date(payment.paymentDate).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2 text-right font-medium">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-3 py-2">
                        {formatPaymentMethod(payment.method)}
                      </td>
                      <td
                        className="max-w-[220px] truncate px-3 py-2 text-muted-foreground"
                        title={payment.note ?? ""}
                      >
                        {payment.note?.trim() || "—"}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {payment.createdBy?.fullName?.trim() ||
                          payment.createdBy?.email ||
                          "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => void removePayment(payment)}
                          disabled={deletingPaymentId === payment.id}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between rounded-md border bg-muted/20 px-3 py-2 text-sm">
            <span className="text-muted-foreground">
              {t("clientDetail.paidTotal")}
            </span>
            <span className="font-semibold">
              {formatCurrency(paymentsTotal)}
            </span>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closePaymentsList}
              disabled={Boolean(deletingPaymentId)}
            >
              {t("common.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
